import { getSoundPreferences } from "@/lib/audio/preferences";
import type { Locale } from "@/lib/i18n";

const VOICE_LANG: Record<Locale, string> = { en: "en-US", fa: "fa-IR", ar: "ar-SA" };

const MAX_QUEUE = 3;

function synthesiser(): SpeechSynthesis | null {
  return globalThis.speechSynthesis ?? null;
}

export function voiceFor(locale: Locale): SpeechSynthesisVoice | null {
  const speech = synthesiser();
  if (speech === null) return null;
  const wanted = locale;
  const voices = speech.getVoices().filter((voice) => voice.lang.toLowerCase().startsWith(wanted));
  if (voices.length === 0) return null;

  return voices.find((voice) => voice.localService) ?? voices[0] ?? null;
}

export function onVoicesChanged(listener: () => void): () => void {
  const speech = synthesiser();
  if (speech === null) return () => undefined;
  speech.addEventListener("voiceschanged", listener);
  return () => {
    speech.removeEventListener("voiceschanged", listener);
  };
}

const queue: { text: string; locale: Locale }[] = [];
let speaking = false;
let lastText = "";
let lastAt = 0;

function drain(): void {
  const speech = synthesiser();
  if (speech === null || speaking) return;
  const next = queue.shift();
  if (next === undefined) return;

  const voice = voiceFor(next.locale);
  if (voice === null) {
    drain();
    return;
  }

  const utterance = new SpeechSynthesisUtterance(next.text);
  utterance.voice = voice;
  utterance.lang = voice.lang === "" ? VOICE_LANG[next.locale] : voice.lang;
  utterance.volume = getSoundPreferences().volume;

  utterance.rate = 1.1;
  utterance.pitch = 1;

  speaking = true;
  const finish = () => {
    speaking = false;
    drain();
  };
  utterance.addEventListener("end", finish, { once: true });
  utterance.addEventListener("error", finish, { once: true });
  try {
    speech.speak(utterance);
  } catch {
    finish();
  }
}

export function say(text: string, locale: Locale): void {
  const preferences = getSoundPreferences();
  if (!preferences.speech || preferences.volume === 0) return;
  if (text.trim() === "") return;
  if (synthesiser() === null) return;

  const now = Date.now();
  if (text === lastText && now - lastAt < 1000) return;
  lastText = text;
  lastAt = now;

  queue.push({ text, locale });
  while (queue.length > MAX_QUEUE) queue.shift();
  drain();
}

export function stopSpeaking(options?: { finishCurrent?: boolean }): void {
  queue.length = 0;
  lastText = "";
  if (options?.finishCurrent === true) return;
  speaking = false;
  try {
    synthesiser()?.cancel();
  } catch (error) {
    console.error("speech cancel failed:", error);
  }
}
